import posthog, { PostHog } from 'posthog-js';

class PostHogService {
  private static instance: PostHogService;
  private isInitialized = false;

  private constructor() { }

  public static getInstance(): PostHogService {
    if (!PostHogService.instance) {
      PostHogService.instance = new PostHogService();
    }
    return PostHogService.instance;
  }

  // By runtime host rather than import.meta.env, so `vite preview` builds count as dev too.
  private isDevHost(): boolean {
    if (typeof window === 'undefined' || !window.location) {
      return false;
    }

    const { hostname } = window.location;
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0' ||
      hostname === '::1' ||
      hostname === '[::1]' ||
      hostname.endsWith('.local')
    );
  }

  public init(): void {
    const apiKey = import.meta.env.VITE_POSTHOG_API_KEY;
    // `/ingest/*` is reverse-proxied to PostHog EU by vercel.json so ad blockers can't drop events.
    const host = import.meta.env.DEV
      ? import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com'
      : '/ingest';

    if (!apiKey) {
      console.warn('PostHog API key not configured. Analytics will be disabled.');
      return;
    }

    if (this.isInitialized) {
      console.warn('PostHog already initialized');
      return;
    }

    try {
      const apiHostname = new URL(
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000',
        window.location.origin,
      ).hostname;

      posthog.init(apiKey, {
        api_host: host,
        ui_host: 'https://eu.posthog.com', // Toolbar/links resolve to EU cloud when captured via the proxy
        opt_in_site_apps: true,
        person_profiles: 'identified_only',
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: false,
        capture_exceptions: true,
        // Sends X-POSTHOG-SESSION-ID / X-POSTHOG-DISTINCT-ID so backend errors, logs and
        // traces link to this user's session replay.
        tracing_headers: [apiHostname],
        logs: {
          serviceName: 'cohaven-web',
          environment: import.meta.env.MODE,
          serviceVersion: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA,
        },
        // Keeps local runs (and broken HMR states) out of the shared project's events and error tracking.
        before_send: (event) => {
          if (event && this.isDevHost()) {
            return null;
          }
          return event;
        },
        loaded: (ph) => {
          if (import.meta.env.DEV) {
            console.log('PostHog loaded successfully');
            ph.debug();
          }
        },
      });

      this.isInitialized = true;
      console.log('PostHog initialized');
    } catch (error) {
      console.error('Failed to initialize PostHog:', error);
    }
  }

  public get client(): PostHog {
    return posthog;
  }

  public get isEnabled(): boolean {
    return this.isInitialized && !!import.meta.env.VITE_POSTHOG_API_KEY;
  }

  public identifyUser(userId: string, properties?: Record<string, unknown>): void {
    if (!this.isEnabled) {
      return;
    }

    try {
      posthog.identify(userId, properties);
      console.log('User identified:', userId);
    } catch (error) {
      console.error('Failed to identify user:', error);
    }
  }

  public reset(): void {
    if (!this.isEnabled) {
      return;
    }

    try {
      posthog.reset();
      console.log('PostHog reset');
    } catch (error) {
      console.error('Failed to reset PostHog:', error);
    }
  }

  public setUserProperties(properties: Record<string, unknown>): void {
    if (!this.isEnabled) {
      return;
    }

    try {
      posthog.setPersonProperties(properties);
    } catch (error) {
      console.error('Failed to set user properties:', error);
    }
  }

  public trackEvent(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.isEnabled) {
      return;
    }

    try {
      posthog.capture(eventName, properties);
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }
}

export const postHogService = PostHogService.getInstance();
export default postHogService;
